// Khung chung + thành phần hiển thị dùng lại cho các màn thẻ liệu trình
import React from 'react';
import { ConfigProvider, Progress, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ScanLine, TicketPlus } from 'lucide-react';
import dayjs from 'dayjs';
import Layout from '../../Layout';
import { DemoRoleSwitch, posTheme } from '../POS/components/PosShell';
import { money, moneyShort } from '../POS/utils';
import type { CardStatus, TreatmentCard } from './data';
import { cardBalance, type CardFlags, type InstallmentState } from './logic';
import { useTreatmentAuth, useTreatmentSync } from './actions';
import { usePayRequestNotifier } from './payRequestNotifier';

export { money, moneyShort };
export const fmtDate = (d: string) => dayjs(d).format('DD/MM/YYYY');
export const fmtDateTime = (d: string) => dayjs(d).format('DD/MM/YYYY HH:mm');

export const CARD_STATUS: Record<CardStatus, { label: string; cls: string }> = {
  active: { label: 'Đang dùng', cls: 'bg-green-50 text-green-700 border-green-200' },
  expired: { label: 'Hết hạn', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  completed: { label: 'Đã dùng hết', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  refunded: { label: 'Đã hoàn tiền', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  cancelled: { label: 'Đã hủy', cls: 'bg-red-50 text-red-600 border-red-200' },
};

export const INSTALLMENT_STATE: Record<InstallmentState, { label: string; cls: string }> = {
  paid: { label: 'Đã thu', cls: 'bg-green-50 text-green-700 border-green-200' },
  overdue: { label: 'Quá hạn', cls: 'bg-red-50 text-red-600 border-red-200' },
  due_soon: { label: 'Sắp đến hạn', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  upcoming: { label: 'Chưa đến hạn', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
  void: { label: 'Không thu', cls: 'bg-slate-50 text-slate-400 border-slate-200' },
};

export const Pill = ({ label, cls, title }: { label: React.ReactNode; cls: string; title?: string }) => (
  <span title={title} className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>
);

/** Thẻ cảnh báo: sắp hết buổi, sắp hết hạn, nợ quá hạn, sắp đến hạn, chờ hoàn */
export function FlagPills({ f, compact }: { f: CardFlags; compact?: boolean }) {
  const out: React.ReactNode[] = [];
  if (f.overdue) out.push(<Pill key="o" cls="bg-red-600 text-white border-red-600" label={compact ? 'Nợ quá hạn' : `Quá hạn ${moneyShort(f.overdueAmount)}`} title={`Trễ ${f.maxOverdueDays} ngày`} />);
  else if (f.dueSoon) out.push(<Pill key="d" cls="bg-amber-50 text-amber-700 border-amber-200" label="Sắp đến hạn đóng" />);
  if (f.low) out.push(<Pill key="l" cls="bg-orange-50 text-orange-700 border-orange-200" label="Sắp hết thẻ" />);
  if (f.expiring) out.push(<Pill key="e" cls="bg-yellow-50 text-yellow-700 border-yellow-200" label={f.daysToExpire <= 0 ? 'Hết hạn hôm nay' : `Hết hạn sau ${f.daysToExpire} ngày`} />);
  if (f.pendingRefund) out.push(<Pill key="r" cls="bg-violet-50 text-violet-700 border-violet-200" label="Chờ hoàn tiền" />);
  return out.length ? <span className="inline-flex flex-wrap gap-1">{out}</span> : null;
}

/** Số dư thẻ: thanh tiến độ + "còn x/y buổi" hoặc "còn x / mệnh giá" */
export function BalanceCell({ card, wide }: { card: TreatmentCard; wide?: boolean }) {
  const b = cardBalance(card);
  const pct = Math.round(b.pctLeft * 100);
  const color = pct <= 20 ? '#f97316' : '#3e91ab';
  const text = card.kind === 'session'
    ? <><b>{b.remainingSessions}</b>/{b.totalSessions} buổi</>
    : <><b>{moneyShort(b.remainingValue)}</b> / {moneyShort(b.faceValue)}</>;
  return (
    <div className={wide ? 'w-full' : 'w-36'}>
      <div className="flex items-baseline justify-between text-xs text-slate-600"><span>Còn {text}</span></div>
      <Progress percent={pct} showInfo={false} size="small" strokeColor={color} className="!m-0" />
    </div>
  );
}

export function Kpi({ label, value, sub, tone = 'default', onClick }: { label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: 'default' | 'warn' | 'danger' | 'ok'; onClick?: () => void }) {
  const toneCls = { default: 'text-slate-800', warn: 'text-amber-600', danger: 'text-red-600', ok: 'text-green-700' }[tone];
  return (
    <button type="button" onClick={onClick} disabled={!onClick}
      className={`rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm ${onClick ? 'cursor-pointer hover:border-[#3e91ab]' : 'cursor-default'}`}>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-extrabold ${toneCls}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </button>
  );
}

interface ShellProps {
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  onSell?: () => void;
  children: React.ReactNode;
}

export default function TreatmentShell({ title, subtitle, actions, onSell, children }: ShellProps) {
  useTreatmentSync();
  const navigate = useNavigate();
  usePayRequestNotifier(cardId => navigate(`/treatment/cards?id=${cardId}`));
  const { can } = useTreatmentAuth();
  return (
    <ConfigProvider theme={posTheme}>
      <Helmet><title>{`${title} | Khothi`}</title></Helmet>
      <Layout>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[18px] font-bold text-000000e0">{title}</h1>
            {subtitle && <div className="mt-0.5 text-sm text-slate-500">{subtitle}</div>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DemoRoleSwitch />
            {actions}
            {can('create') && (
              <Tooltip title="Tìm khách, trừ buổi, check-in lịch hẹn">
                <button onClick={() => navigate('/treatment/checkin')}
                  className="flex h-9 items-center gap-1.5 rounded-lg border border-[#357f96] px-3 text-sm font-semibold text-[#357f96] hover:bg-[#ecf6f9]">
                  <ScanLine className="h-4 w-4" /> Trừ buổi
                </button>
              </Tooltip>
            )}
            {can('create') && onSell && (
              <button onClick={onSell} className="flex h-9 items-center gap-1.5 rounded-lg bg-[#357f96] px-3 text-sm font-semibold text-white hover:bg-[#2c6b7f]">
                <TicketPlus className="h-4 w-4" /> Bán thẻ
              </button>
            )}
          </div>
        </div>
        {children}
      </Layout>
    </ConfigProvider>
  );
}
